import { GraphQLFetchPolicies } from 'shared/enums';
import {
  EmailTemplateType,
  useCreateEmailTemplateVersionMutation,
  useFindCurrentEmailTemplateVersionsQuery,
  useSaveEmailTemplateVersionMutation,
  useUpdateOneEmailTemplateVersionMutation,
  SaveEmailTemplateVersionInput as SaveInput,
  CreateEmailTemplateVersionInput as CreateInput,
  EmailTemplateVersionUpdateInput as UpdateInput,
  EmailTemplateVersionWhereUniqueInput as WhereInput,
} from 'shared/types/graphql';
import { EmailTemplateVersionFragment, UnlayerUser } from '../../../types/graphql';

export const useEmailTemplates = (
  type: EmailTemplateType,
): {
  draft: EmailTemplateVersionFragment | null | undefined;
  published: EmailTemplateVersionFragment | null | undefined;
  userGroup: UnlayerUser | null | undefined;
  loading: boolean;
  saving: boolean;
  updating: boolean;
  createEmailTemplate: (data: CreateInput) => Promise<string | undefined>;
  saveEmailTemplate: (data: SaveInput) => Promise<void>;
  discardDraftEmailTemplate: () => Promise<void>;
} => {
  const {
    data,
    loading: isFindingEmailTemplate,
    refetch,
  } = useFindCurrentEmailTemplateVersionsQuery({
    variables: { data: { type } },
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const [create, { loading: isCreatingEmailTemplate }] = useCreateEmailTemplateVersionMutation();
  const [save, { loading: isSavingEmailTemplate }] = useSaveEmailTemplateVersionMutation();
  const [update, { loading: isUpdatingEmailTemplate }] = useUpdateOneEmailTemplateVersionMutation();

  const templateData = data?.findCurrentEmailTemplateVersions;

  const createEmailTemplate = async (data: CreateInput): Promise<string | undefined> => {
    const result = await create({ variables: { data } });
    const id = result.data?.createEmailTemplateVersion.id;
    refetch();
    return id;
  };

  const saveEmailTemplate = async (data: SaveInput): Promise<void> => {
    await save({ variables: { data } }).then(() => refetch());
  };

  const discardDraftEmailTemplate = async (): Promise<void> => {
    const data: UpdateInput = { deletedAt: new Date().toISOString() };
    const where: WhereInput = { id: templateData?.draft?.id };
    await update({ variables: { data, where } }).then(() => refetch());
  };

  return {
    draft: templateData?.draft,
    published: templateData?.published,
    userGroup: templateData?.unlayer,
    loading: isFindingEmailTemplate,
    saving: isCreatingEmailTemplate || isSavingEmailTemplate,
    updating: isUpdatingEmailTemplate,
    createEmailTemplate,
    saveEmailTemplate,
    discardDraftEmailTemplate,
  };
};
